/**
 * Calendar Header Component
 *
 * Displays the calendar page title, the pending change requests shortcut,
 * the create button and the auto-refresh toggle.
 */

import React from 'react';
import { Button, Badge, theme } from 'antd';
import { CalendarOutlined, FileSearchOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AutoRefreshToggle } from '@/components/ui/AutoRefreshToggle';
import { PageHeader } from '@/components/ui/PageHeader';

interface CalendarHeaderProps {
  /** Whether auto-refresh is currently enabled */
  autoRefresh: boolean;
  /** Callback when auto-refresh toggle is changed */
  onAutoRefreshChange: (enabled: boolean) => void;
  /** Callback when create appointment button is clicked */
  onCreateAppointment?: () => void;
  /** Whether data is currently being fetched */
  isFetching?: boolean;
  /** Last updated timestamp */
  lastUpdated?: string;
  /** Count of pending customer reschedule requests */
  pendingChangeRequestCount?: number;
  /** Opens the pending change requests drawer */
  onOpenPendingRequests?: () => void;
}

/**
 * Header component for the calendar page
 * Shows title and auto-refresh control
 */
export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  autoRefresh,
  onAutoRefreshChange,
  onCreateAppointment,
  isFetching = false,
  lastUpdated,
  pendingChangeRequestCount = 0,
  onOpenPendingRequests
}) => {
  const { t } = useTranslation('manage');
  const { token } = theme.useToken();
  const showPendingRequests = pendingChangeRequestCount > 0 && onOpenPendingRequests;

  return (
    <PageHeader
      icon={<CalendarOutlined />}
      title={t('appointmentComponents.calendarHeader.title')}
      description={t('appointmentComponents.calendarHeader.description')}
      actions={
        <>
          {showPendingRequests ? (
            <Badge count={pendingChangeRequestCount} color={token.colorWarning}>
              <Button icon={<FileSearchOutlined />} onClick={onOpenPendingRequests}>
                {t('appointmentComponents.changeRequest.pendingHeaderButton')}
              </Button>
            </Badge>
          ) : null}
          {onCreateAppointment && (
            <Button type="primary" icon={<PlusOutlined />} onClick={onCreateAppointment}>
              {t('appointmentComponents.calendarHeader.createAppointment')}
            </Button>
          )}
          <AutoRefreshToggle autoRefresh={autoRefresh} onAutoRefreshChange={onAutoRefreshChange} isFetching={isFetching} lastUpdated={lastUpdated} />
        </>
      }
    />
  );
};
